import { useEffect, useState } from "react";
import { SignaturePad } from "@/components/SignaturePad";
import { CONSENT } from "@/constants/testIds";
import { X, ShieldCheck } from "lucide-react";

/**
 * Consent modal — 3 required checkboxes + signature pad.
 * Cannot be skipped: confirm button disabled until all required + sig drawn.
 */
export function ConsentModal({ clientName, onClose, onConfirm }) {
  const [allergies, setAllergies] = useState(false);
  const [history, setHistory] = useState(false);
  const [cancellation, setCancellation] = useState(false);
  const [sig, setSig] = useState(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const allOk = allergies && history && cancellation && !!sig;

  const submit = () => {
    if (!allOk) return;
    onConfirm({
      signature_data_url: sig,
      allergies_disclosed: allergies,
      history_consent: history,
      cancellation_policy: cancellation,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center anim-fade-in"
      style={{ background: "rgba(28,25,23,0.45)", backdropFilter: "blur(12px)" }}
      data-testid={CONSENT.modal}
    >
      <div className="glass-elevated rounded-[24px] p-6 w-[min(480px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,168,83,0.18)" }}>
            <ShieldCheck size={20} className="text-[var(--color-gold-deeper)]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-[20px] font-bold tracking-[-0.02em]">One quick step before your booking</h2>
            {clientName && (
              <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-0.5">
                For {clientName}
              </p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full glass-subtle flex items-center justify-center" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="space-y-2.5">
          <Check label="I have disclosed all known allergies and medical conditions"
                 value={allergies} onChange={setAllergies} testId={CONSENT.allergiesCheckbox} />
          <Check label="I consent to the salon storing my service history and formulas for future visits"
                 value={history} onChange={setHistory} testId={CONSENT.historyCheckbox} />
          <Check label="I understand the 24-hour cancellation policy"
                 value={cancellation} onChange={setCancellation} testId={CONSENT.cancellationCheckbox} />
        </div>

        <div>
          <div className="font-inter text-[13px] font-medium text-[var(--color-text-muted)] mb-1.5">Your signature</div>
          <SignaturePad onChange={setSig} />
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={!allOk}
          data-testid={CONSENT.confirmBtn}
          className="btn-3d btn-gold-3d w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          I Agree & Confirm Booking
        </button>
      </div>
    </div>
  );
}

function Check({ label, value, onChange, testId }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="mt-0.5 w-4 h-4 rounded border-2 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)] focus:ring-[var(--color-mauve)]/30 cursor-pointer"
      />
      <span className="font-inter text-[13px] text-[var(--color-text-secondary)] leading-snug">{label}</span>
    </label>
  );
}

export default ConsentModal;
